import { useState, useEffect, useCallback } from 'react';
import { isLoggedIn, getUserEmail, logout, subscribeToAuthChanges } from '@services/auth';
import { useTranslation } from 'react-i18next';
import { IconLogout } from '@tabler/icons-react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Logo } from './Logo';
import { BrandText } from '@components/BrandText';
import { ThemeSelector } from '@components/ThemeSelector';
import { LanguageSwitcher } from '@components/LanguageSwitcher';
import { getCurrentSubdomain } from '@utils/subdomain';
import './Header.css';

function Header() {
  const { t } = useTranslation();
  const [loggedIn, setLoggedIn] = useState(() => isLoggedIn());
  const [email, setEmail] = useState(() => getUserEmail());
  const [logoCollapsed, setLogoCollapsed] = useState(false);
  const [hasSubdomain, setHasSubdomain] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');
  const navigate = useNavigate();
  const location = useRouterState({
    select: (state) => state.location,
  });
  const isDownloadRoute = location.pathname?.startsWith('/d/');
  const isUploadRoute = location.pathname === '/upload';
  const isMinimalHeader = isDownloadRoute || isUploadRoute || hasSubdomain;
  const handleLogoComplete = useCallback(() => {
    setLogoCollapsed(true);
  }, [setLogoCollapsed]);

  useEffect(() => {
    const handleAuthChange = () => {
      setLoggedIn(isLoggedIn());
      setEmail(getUserEmail());
    };

    const unsubscribe = subscribeToAuthChanges(handleAuthChange);
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    setHasSubdomain(Boolean(getCurrentSubdomain()));
  }, []);
  
  if (isMinimalHeader) {
    return (
      <header className="header header--download">
        <div className="header-download-spacer" aria-hidden="true" />
        <Link
          to="/"
          className={`logo-link logo-link--center${logoCollapsed ? ' logo-link--collapsed' : ''}`}
        >
          <span
            className={`header-logo${logoCollapsed ? ' header-logo--collapsed' : ''}`}
            aria-label={t('brand.name')}
          >
            <Logo animated variant="default" onAnimationComplete={handleLogoComplete} />
          </span>
        </Link>
        <div className="header-minimal-right">
          <LanguageSwitcher />
          <div className="header-theme-toggle header-theme-toggle--download">
            <ThemeSelector />
          </div>
          {loggedIn ? (
            <div className="header-user header-user--minimal">
              <Link to="/dashboard" className="header-link header-link--minimal">
                {email ?? t('nav.account')}
              </Link>
              <button
                type="button"
                className="header-logout header-logout--minimal"
                aria-label={t('nav.logout')}
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                  navigate({ to: '/' });
                }}
              >
                <IconLogout size={18} stroke={2} />
              </button>
            </div>
          ) : (
            <Link to="/login" className="header-link header-link--minimal">
              {t('nav.login')}
            </Link>
          )}
        </div>
      </header>
    );
  }

  return (
    <header className="header">
      <div className="header-left">
        <Link
          to="/"
          className={`logo-link${logoCollapsed ? ' logo-link--collapsed' : ''}`}
        >
          <span
            className={`header-logo${logoCollapsed ? ' header-logo--collapsed' : ''}`}
            aria-label={t('brand.name')}
          >
            <Logo
              animated
              variant="default"
              onAnimationComplete={handleLogoComplete}
            />
          </span>
        </Link>
        <nav className="header-nav">
          <Link to="/" className="header-link">
            {t('nav.home')}
          </Link>
          <Link to="/" hash="why" className="header-link">
            <BrandText text={t('nav.why')} />
          </Link>
          <Link to="/" hash="pricing" className="header-link">{t('nav.pricing')}</Link>
          <Link to="/blog" className="header-link">
            {t('nav.blog', 'Blog')}
          </Link>
        </nav>
      </div>
      <div className="header-right">
        <div className="header-theme-toggle">
          <ThemeSelector />
        </div>
        <LanguageSwitcher />
        {loggedIn ? (
          <>
            <Tooltip label={email} disabled={!isMobile || !email}>
              <Link to="/dashboard" className="header-link">
                {isMobile ? t('nav.myAccount') : (email ?? t('nav.account'))}
              </Link>
            </Tooltip>
            <button
              type="button"
              className="header-logout"
              aria-label={t('nav.logout')}
              onClick={(e) => {
                e.preventDefault();
                logout();
                navigate({ to: '/' });
              }}
            >
              <IconLogout size={18} stroke={2} />
            </button>
          </>
        ) : (
          <Link to="/login" className="header-link">{t('nav.login')}</Link>
        )}
      </div>
    </header>
  );
}

export default Header;
